/**
 * StateProgrammes — /states/programmes
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { PageHeader, SectionCard, CTAButton } from "@/components/ds";

export default function StateProgrammes() {
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", sector: "", programme_type: "poa", target_reduction: "" });
  const { stateId, geography: geo, stateParams } = useStateContext();

  const load = () => {
    client.get(`/states/programmes${stateParams}`)
      .then(r => setProgrammes(r.data?.programmes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [geo]);

  const handleCreate = async () => {
    if (!form.name) { toast.error("Programme name required."); return; }
    setCreating(true);
    try {
      await client.post("/states/programmes", {
        name: form.name, geography: geo, sector: form.sector,
        programme_type: form.programme_type,
        target_reduction_tco2e: form.target_reduction ? parseFloat(form.target_reduction) : null,
      });
      toast.success("Programme created.");
      setForm({ name: "", sector: "", programme_type: "poa", target_reduction: "" });
      load();
    } catch { toast.error("Failed to create programme."); }
    setCreating(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Programme Structuring"
        subtitle={`${geo} · PoA / CPA / State NDC programmes`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <SectionCard title="Create Programme">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Programme Name</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              placeholder="e.g. Nigeria Solar PoA"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Type</label>
            <select value={form.programme_type} onChange={e => setForm({...form, programme_type: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl">
              <option value="poa">Programme of Activities (PoA)</option>
              <option value="cpa">Component Project Activity (CPA)</option>
              <option value="state_ndc">State NDC Programme</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Sector</label>
            <input value={form.sector} onChange={e => setForm({...form, sector: e.target.value})}
              placeholder="e.g. Energy, Agriculture"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Target Reduction (tCO₂e/yr)</label>
            <input type="number" value={form.target_reduction}
              onChange={e => setForm({...form, target_reduction: e.target.value})}
              placeholder="Optional"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl" />
          </div>
        </div>
        <CTAButton variant="action" icon={Plus} loading={creating} onClick={handleCreate}>
          {creating ? "Creating..." : "Create Programme"}
        </CTAButton>
      </SectionCard>

      {programmes.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <MetricCard label="Total Programmes" value={programmes.length} color="text-slate-900" />
          <MetricCard label="Submitted" value={programmes.filter(p => p.status === "submitted").length} color="text-teal-700" />
          <MetricCard label="Draft" value={programmes.filter(p => p.status === "draft").length} color="text-amber-600" />
        </div>
      )}

      {programmes.length === 0 ? (
        <SectionCard>
          <div className="py-8 text-center">
            <p className="text-sm text-slate-500">No programmes yet — create the first one above.</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-3">
          {programmes.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{p.ref_id} · {p.programme_type?.toUpperCase()} · {p.sector}</p>
                  {p.total_reduction_tco2e && (
                    <p className="text-xs text-teal-600 font-medium mt-1">
                      Target: {p.total_reduction_tco2e?.toFixed(0)} tCO₂e/yr
                    </p>
                  )}
                </div>
                <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 ${
                  p.status === "draft"      ? "bg-slate-100 text-slate-600" :
                  p.status === "submitted"  ? "bg-teal-100 text-teal-700" :
                  "bg-emerald-100 text-emerald-700"
                }`}>{p.status}</span>
              </div>
              {p.status === "draft" && (
                <button
                  onClick={async () => {
                    try {
                      const res = await client.post("/states/submit-to-fi-pipeline", {
                        programme_id: p.id, fi_notes: "Submitted from States workspace"
                      });
                      toast.success(`Programme submitted to FI pipeline as ${res.data.deal_id}`);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Submission failed.");
                    }
                  }}
                  className="mt-3 text-xs font-semibold text-teal-600 hover:underline flex items-center gap-1"
                  data-testid={`submit-fi-${p.id}`}
                >
                  → Submit to FI Pipeline
                </button>
              )}
              {p.status === "submitted" && p.fi_request_id && (
                <p className="text-xs text-teal-600 mt-2">
                  ✓ Submitted — Deal: {p.fi_request_id}
                  <button onClick={() => navigate("/fi")} className="ml-2 hover:underline font-semibold">View in FI Workspace →</button>
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
